import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { auditoriaProveedoresApi } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Skeleton } from '../../components/ui/skeleton';
import { ShieldCheck, RefreshCw, Play, Wand2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { TablaNombres } from './auditoria-nombres/TablaNombres';
import { TablaRut } from './auditoria-nombres/TablaRut';
import { TablaHistorial } from './auditoria-nombres/TablaHistorial';
import { TablaPagoOC } from './auditoria-nombres/TablaPagoOC';

const fmtFecha = (iso) => (iso ? new Date(iso).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' }) : '—');

export default function AuditoriaNombresProveedores() {
  const navigate = useNavigate();
  const [data, setData] = useState({ items: [], resumen: null, conteos_pendientes: {} });
  const [historial, setHistorial] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [batch, setBatch] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, h] = await Promise.all([
        auditoriaProveedoresApi.list({ estado: 'pendiente' }),
        auditoriaProveedoresApi.list({ estado: 'todos' }),
      ]);
      setData(p.data);
      setHistorial((h.data.items || []).filter((i) => i.estado !== 'pendiente'));
    } catch (e) {
      toast.error('No se pudo cargar la auditoría', { description: e.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const ejecutar = async () => {
    setRunning(true);
    try {
      const r = await auditoriaProveedoresApi.ejecutar();
      toast.success('Auditoría ejecutada', { description: `${r.data.detectadas} discrepancias (${r.data.nuevas} nuevas), ${r.data.pendientes} pendientes` });
      await load();
    } catch (e) {
      toast.error('Error al ejecutar la auditoría', { description: e.response?.data?.detail || e.message });
    } finally {
      setRunning(false);
    }
  };

  const corregirRutTodos = async () => {
    if (!window.confirm('¿Corregir en Alegra y en el ERP todos los RUT con formato distinto a 00000000-A? (solo los que tienen dígito verificador válido)')) return;
    setBatch(true);
    try {
      const r = await auditoriaProveedoresApi.corregirRutTodos();
      toast[r.data.errores ? 'warning' : 'success'](`${r.data.corregidos} RUT corregidos`, {
        description: r.data.errores ? `${r.data.errores} con error (ver detalle en cada fila)` : 'Todos los RUT quedaron en formato 00000000-A',
      });
      await load();
    } catch (e) {
      toast.error('Error en la corrección masiva', { description: e.response?.data?.detail || e.message });
    } finally {
      setBatch(false);
    }
  };

  const items = data.items || [];
  const distintos = items.filter((i) => i.clase === 'nombre' && i.tipo === 'nombre_distinto');
  const variantes = items.filter((i) => i.clase === 'nombre' && i.tipo === 'variante_menor');
  const ruts = items.filter((i) => i.clase === 'rut');
  const pagosOC = items.filter((i) => i.clase === 'pago_oc');
  const r = data.resumen;

  return (
    <div className="p-4 space-y-4" data-testid="auditoria-nombres-page">
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="w-6 h-6" /> Auditoría: RUT, Razón Social y Datos de Pago
          </h1>
          <p className="text-sm text-muted-foreground" data-testid="auditoria-resumen">
            Compara el ERP con los documentos legales de los últimos 30 días (DTE como fuente principal, RCV/LCV como segunda) y revisa que toda OC desde el 24-07-2026 tenga condición vigente y medio de pago.
            {r ? ` Última corrida: ${fmtFecha(r.last_run)} (${r.trigger === 'cron' ? 'nocturna' : 'manual'}) · ventana desde ${r.ventana_desde} · ${r.pendientes} pendientes.` : ' Aún no se ha ejecutado.'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => navigate('/compras/proveedores')} data-testid="btn-volver-proveedores">Volver a Proveedores</Button>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="btn-auditoria-refrescar">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refrescar
          </Button>
          <Button size="sm" onClick={ejecutar} disabled={running} data-testid="btn-auditoria-ejecutar">
            <Play className={`w-4 h-4 mr-1 ${running ? 'animate-pulse' : ''}`} /> {running ? 'Ejecutando…' : 'Ejecutar ahora'}
          </Button>
        </div>
      </div>

      {loading && !items.length ? (
        <div className="space-y-2"><Skeleton className="h-10 w-full" /><Skeleton className="h-40 w-full" /></div>
      ) : (
        <Tabs defaultValue="distintos">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="distintos" data-testid="tab-nombres-distintos">Nombre distinto <Badge variant="destructive" className="ml-2">{distintos.length}</Badge></TabsTrigger>
            <TabsTrigger value="variantes" data-testid="tab-variantes-menores">Variantes menores <Badge variant="secondary" className="ml-2">{variantes.length}</Badge></TabsTrigger>
            <TabsTrigger value="rut" data-testid="tab-formato-rut">Formato RUT <Badge variant="secondary" className="ml-2">{ruts.length}</Badge></TabsTrigger>
            <TabsTrigger value="pago-oc" data-testid="tab-pago-oc">Datos de pago OC <Badge variant="destructive" className="ml-2">{pagosOC.length}</Badge></TabsTrigger>
            <TabsTrigger value="historial" data-testid="tab-historial">Corregidas / Ignoradas <Badge variant="outline" className="ml-2">{historial.length}</Badge></TabsTrigger>
          </TabsList>
          <TabsContent value="distintos">
            <p className="text-xs text-muted-foreground mb-2">El nombre legal del ERP (lo que sigue al "…)" si hay nombre de fantasía) no coincide con la razón social del documento.</p>
            <TablaNombres items={distintos} onChange={load} testid="tabla-nombres-distintos" />
          </TabsContent>
          <TabsContent value="variantes">
            <p className="text-xs text-muted-foreground mb-2">Solo difieren en puntuación, tildes o sufijos societarios (LTDA. / LIMITADA, S.A. / SA). No cuentan como discrepancia real, pero puedes alinearlas.</p>
            <TablaNombres items={variantes} onChange={load} testid="tabla-variantes-menores" />
          </TabsContent>
          <TabsContent value="rut">
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <p className="text-xs text-muted-foreground">RUT fuera del formato obligatorio <code>00000000-A</code> (ceros a la izquierda, DV en mayúscula, con guion). Los inválidos (DV incorrecto) requieren corrección manual.</p>
              {ruts.some((i) => i.rut_canonico) && (
                <Button size="sm" variant="outline" onClick={corregirRutTodos} disabled={batch} data-testid="btn-corregir-rut-todos">
                  <Wand2 className="w-4 h-4 mr-1" /> {batch ? 'Corrigiendo…' : `Corregir todos (${ruts.filter((i) => i.rut_canonico).length})`}
                </Button>
              )}
            </div>
            <TablaRut items={ruts} onChange={load} />
          </TabsContent>
          <TabsContent value="pago-oc">
            <p className="text-xs text-muted-foreground mb-2">OCs desde el 24-07-2026 (no anuladas) sin condición vigente, con nombre ≠ código, o Contado/Tarjeta sin Medio de Pago. No se corrigen automáticamente: edita la OC y completa los datos (sin ellos no se puede grabar ni dar VB).</p>
            <TablaPagoOC items={pagosOC} onChange={load} />
          </TabsContent>
          <TabsContent value="historial">
            <TablaHistorial items={historial} onChange={load} />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}
